import {useNavigate} from "react-router-dom";
import Header from "@components/Header.jsx";
import { collection, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import {  db } from "../firebaseConfig.js";
import {useMemo, useState} from "react";
import {useAuth} from "@/utils/auth.jsx";
import {Table, Tag, Alert, Input} from 'antd';
import {
    UnorderedListOutlined,
    AndroidOutlined,
    AppleOutlined,
    StarOutlined,
    StopOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import Button from "@components/Button.jsx";
import useUserApps from "@/utils/useUserApps.js";
import { useLockedFeature } from "@components/LockedFeature.jsx";
import { useCurrentUserFromFirestore } from "@/utils/useCurrentUserFromFirestore.js";

const AppPage = () => {
    const navigate = useNavigate();
    const {user} = useAuth();
    // Accounts approved under the old moderation flow are grandfathered in
    // alongside newly email-verified accounts.
    const { firestoreUser } = useCurrentUserFromFirestore();
    const isActive = !!user?.emailVerified || firestoreUser?.status === "active";

    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState("");
    const { openLocked, lockedModal } = useLockedFeature();


    const { data, loading, refetchApplications } = useUserApps (user);

    const filteredData = useMemo(() => {
        if (!search.trim()) return data;
        return data.filter((app) =>
            app.appName?.toLowerCase().includes(search.trim().toLowerCase())
        );
    }, [data, search]);

    const filterTabs = [
        { key: "all", label: "All", count: data.length, real: true },
        { key: "android", label: "Android", icon: <AndroidOutlined />, count: 0 },
        { key: "ios", label: "iOS", icon: <AppleOutlined />, count: 0 },
        { key: "favorite", label: "Favorite", icon: <StarOutlined />, count: 0 },
        { key: "banned", label: "Banned", icon: <StopOutlined />, count: 0 },
    ];

    const columns = [
        {
            title: 'Name',
            dataIndex: 'appName',
            render: (text) => <a>{text}</a>,
            key: 'appName'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, { status }) => {
                let color;
                switch (status.toLowerCase()) {
                    case 'moderation':
                        color = 'orange';
                        break;
                    case 'finished':
                        color = 'green';
                        break;
                    case 'on process':
                        color = 'blue';
                        break;
                    case 'rejected':
                        color = 'volcano';
                        break;
                    default:
                        color = 'gray';
                }
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }


        },
        {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (timestamp) => {
                if (!timestamp) return 'N/A';

                // If timestamp is Firestore Timestamp object
                const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);

                return dateObj.toLocaleString(); // format as you like
            },
        },
        {
            title: 'Edit',
            key: 'x',
            render: (_, record) =>
                isActive ? (
                    <a onClick={() => navigate('/create-app', { state: { app: record } })}>
                        Edit
                    </a>
                ) : null,
        },
        {
            title: 'Delete',
            dataIndex: '',
            key: 'delete',
            render: (_, record) =>
                isActive ? (
                    <a
                        style={{ color: "red" }}
                        onClick={async () => {
                            try {
                                await deleteDoc(doc(db, "apps", record.id));
                                setSuccess(true);
                                await refetchApplications();
                            } catch (error) {
                                console.error("Failed to delete app:", error);
                                setError(true);
                            }
                        }}
                    >
                        Delete
                    </a>
                ) : null,
        }
    ];


    return (
        <div className="app-page">
            {success && (
                <Alert
                    message="App deleted successfully!"
                    type="success"
                    showIcon
                    closable
                    onClose={() => setSuccess(false)}
                />
            )}

            {error && (
                <Alert
                    message="Failed to delete!!"
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(false)}
                />
            )}
            <div className="wrapper">
                <div className={`page page-app` }>
                    <Header title={`App`} />

                    <div className="apps-toolbar">
                        <div className="apps-filter-tabs">
                            {filterTabs.map((tab) => (
                                <button
                                    key={tab.key}
                                    className="apps-filter-tab"
                                    onClick={tab.real ? undefined : openLocked}
                                >
                                    <span className="apps-filter-tab__icon">
                                        {tab.icon ?? <UnorderedListOutlined />}
                                    </span>
                                    <span className="apps-filter-tab__label">{tab.label}</span>
                                    <span className="apps-filter-tab__count">{tab.count}</span>
                                </button>
                            ))}
                            <button className="apps-sort" onClick={openLocked}>
                                Sort by: Favorite
                            </button>
                        </div>

                        <div className="apps-actions-row">
                            <Input.Search
                                className="apps-search"
                                placeholder="Search"
                                allowClear
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                            />
                            {isActive && (
                                <Button onClick={() => navigate('/create-app')} title={<><PlusOutlined /> Add new app</>} />
                            )}
                            <button className="apps-view-toggle" onClick={openLocked} title="Grid view">
                                <UnorderedListOutlined />
                            </button>
                        </div>
                    </div>

                    <Table columns={columns} dataSource={filteredData} />
                </div>
            </div>
            {lockedModal}
        </div>

    )
}

export default AppPage